import React from 'react';
import { Publication } from '../../../hooks/usePublications';
import { mediaThumbnail, mediaLabel, mediaIcon, previewText } from './PublicationMedia';
import LikeShareBar from './LikeShareBar';
import './PublicationCard.css';

interface PublicationCardProps {
  publication: Publication;
  onView: () => void;
  customerId?: string;
  onOpenModal: (publicationId: string) => void;
}

const PublicationCard: React.FC<PublicationCardProps> = ({
  publication,
  onOpenModal,
}) => {
  // Supprimer le tracking automatique à l'affichage de la carte
  // Le tracking se fera uniquement à l'ouverture de la modale (vue détaillée)

  const handleCardClick = () => {
    onOpenModal(publication.id);
  };

  const handleDetailsClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Empêcher la propagation au card click
    onOpenModal(publication.id);
  };

  const getTypeIcon = (type: Publication['type']) => {
    const icons = {
      ARRIVAGE: '🍅',
      PROMOTION: '🎉',
      ANTI_GASPI: '🌱',
      EVENEMENT: '🎪',
      INFO: 'ℹ️',
    };
    return icons[type] || '📢';
  };

  const getTypeLabel = (type: Publication['type']) => {
    const labels = {
      ARRIVAGE: 'Arrivage',
      PROMOTION: 'Promotion',
      ANTI_GASPI: 'Anti-gaspi',
      EVENEMENT: 'Événement',
      INFO: 'Information',
    };
    return labels[type] || type;
  };

  const getTypeBadgeColor = (type: Publication['type']) => {
    const colors = {
      ARRIVAGE: '#10b981', // green
      PROMOTION: '#f59e0b', // amber
      ANTI_GASPI: '#22c55e', // lime
      EVENEMENT: '#8b5cf6', // purple
      INFO: '#3b82f6', // blue
    };
    return colors[type] || '#6b7280';
  };

  const isNew = () => {
    const publishedDate = new Date(publication.published_at || publication.created_at);
    const now = new Date();
    const hoursDiff = (now.getTime() - publishedDate.getTime()) / (1000 * 60 * 60);
    return hoursDiff < 24; // Nouveau si moins de 24h
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Il y a moins d\'une heure';
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;

    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
    });
  };

  // Pour la vignette, on préfère un média dont on connaît l'image (podcast Ausha,
  // YouTube) à un fichier MP4 sans affiche.
  const firstVideo =
    publication.videos?.find((video) => mediaThumbnail(video)) || publication.videos?.[0];
  const videoThumb = firstVideo ? mediaThumbnail(firstVideo) : undefined;
  const preview = publication.summary || previewText(publication.content, 150);

  return (
    <div className="publication-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      {/* Badge "Nouveau" */}
      {isNew() && (
        <div className="new-badge">
          ✨ Nouveau
        </div>
      )}

      {/* Header avec marchand */}
      <div className="publication-header">
        <div className="merchant-info">
          {/* La liste `/publications` ne renvoie pas le marchand : pas de rond vide */}
          {publication.tenant?.name && (
            <div className="merchant-avatar">
              {publication.tenant.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            {publication.tenant?.name && <div className="merchant-name">{publication.tenant.name}</div>}
            <div className="publication-date">
              {formatDate(publication.published_at || publication.created_at)}
            </div>
          </div>
        </div>
        <div
          className="type-badge"
          style={{ backgroundColor: getTypeBadgeColor(publication.type) }}
        >
          {getTypeIcon(publication.type)} {getTypeLabel(publication.type)}
        </div>
      </div>

      {/* Image principale */}
      {publication.images && publication.images.length > 0 && (
        <div className="publication-image">
          <img
            src={publication.images[0].url}
            alt={publication.images[0].caption || publication.title}
            loading="lazy"
          />
        </div>
      )}

      {/* Vidéo ou podcast, quand il n'y a pas d'image : vignette + bouton lecture.
          Le lecteur lui-même s'ouvre dans la vue détaillée. */}
      {(!publication.images || publication.images.length === 0) && firstVideo && (
        <div className="publication-media-thumb">
          {videoThumb && <img src={videoThumb} alt={firstVideo.caption || publication.title} loading="lazy" />}
          <div className="media-play">
            <span>▶</span>
            {mediaIcon(firstVideo)} {mediaLabel(firstVideo)}
          </div>
        </div>
      )}

      {/* Contenu */}
      <div className="publication-content">
        <h3 className="publication-title">{publication.title}</h3>
        {preview && <p className="publication-summary">{preview}</p>}
      </div>

      {/* Produits liés */}
      {publication.products && publication.products.length > 0 && (
        <div className="publication-products">
          <div className="products-title">Produits concernés :</div>
          <div className="products-list">
            {publication.products.slice(0, 3).map((item) => (
              <div key={item.id} className="product-item">
                <div className="product-info">
                  <span className="product-name">{item.product.name}</span>
                  {item.special_offer && (
                    <span className="special-offer">{item.special_offer}</span>
                  )}
                </div>
                {item.highlight_price && item.product.prices?.[0] && (
                  <div className="product-price">
                    {(item.product.prices[0].amount_cents / 100).toFixed(2)} €
                  </div>
                )}
              </div>
            ))}
            {publication.products.length > 3 && (
              <div className="more-products">
                +{publication.products.length - 3} autres produits
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pied de carte : j'aime + partager, puis le détail */}
      <div className="publication-footer">
        <LikeShareBar
          compact
          publicationId={publication.id}
          title={publication.title}
          likesCount={publication.likes_count ?? 0}
          liked={publication.liked}
        />
        <button className="btn-see-more" onClick={handleDetailsClick}>
          Voir les détails →
        </button>
      </div>
    </div>
  );
};

export default PublicationCard;
